import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { 
  User, 
  Course, 
  Evaluation, 
  Progress, 
  LoginRequest, 
  LoginResponse, 
  RegisterRequest,
  ApiResponse,
  DashboardData,
  ReportData
} from '../types';

// Configuración base de axios
// REACT_APP_API_URL en client/.env (ej. http://localhost:5001/api) debe coincidir con PORT del backend
const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5001/api');

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
      },
      timeout: 30000, // 30 segundos de timeout
    });

    // Interceptor para agregar token de autenticación
    this.api.interceptors.request.use(
      (config) => {
        // Rutas que no requieren autenticación
        const publicRoutes = [
          '/auth/login',
          '/auth/register',
          '/auth/forgot-password',
          '/domain-requests', // Para crear solicitudes
          '/email-verification' // Verificación desde enlace del correo (sin JWT; no bloquear si hay token caducado en localStorage)
        ];
        
        // Verificar si la ruta es pública
        let isPublicRoute = publicRoutes.some(route => config.url?.includes(route));
        
        // Verificar si es una actualización de solicitud de dominio (PUT /domain-requests/:id o /by-email)
        // Solo marcar como pública la actualización por email, no las de estado
        if (config.method === 'put' && config.url?.includes('/domain-requests/by-email/')) {
          isPublicRoute = true;
        }
        
        // Si es una ruta pública, no agregar token
        if (isPublicRoute) {
          return config;
        }
        
        const token = localStorage.getItem('token');
        
        // Verificar si el token es válido antes de usarlo
        if (token && this.isTokenValid()) {
          config.headers.Authorization = `Bearer ${token}`;
          
          // Si el token está próximo a expirar, mostrar advertencia
          if (this.isTokenExpiringSoon()) {
            console.warn('Token próximo a expirar, considere renovar la sesión');
          }
        } else if (token && !this.isTokenValid()) {
          // Token expirado, limpiar y redirigir
          console.log('Token expirado, limpiando sesión');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
          return Promise.reject(new Error('Token expirado'));
        }
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Interceptor para manejar errores de autenticación
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        
        // Si es un error 401 y no es un reintento
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          // Intentar renovar el token antes de redirigir
          try {
            const refreshSuccess = await this.refreshToken();
            if (refreshSuccess) {
              // Reintentar la solicitud original con el nuevo token
              const token = localStorage.getItem('token');
              if (token) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return this.api(originalRequest);
              }
            }
          } catch (refreshError) {
            console.log('Error renovando token:', refreshError);
          }
          
          // Si la renovación falla, limpiar sesión
          console.log('Token expirado o inválido');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          
          // Solo redirigir si NO estamos ya en la página de login
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  // Métodos de autenticación
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response: AxiosResponse<ApiResponse<LoginResponse>> = await this.api.post('/auth/login', credentials);
    return response.data.data!;
  }

  async register(userData: RegisterRequest): Promise<LoginResponse> {
    const response: AxiosResponse<ApiResponse<LoginResponse>> = await this.api.post('/auth/register', userData);
    return response.data.data!;
  }

  async getCurrentUser(): Promise<User> {
    const response: AxiosResponse<ApiResponse<{ user: User }>> = await this.api.get('/auth/me');
    return response.data.data!.user;
  }

  // Renovar token automáticamente
  async refreshToken(): Promise<boolean> {
    try {
      // No intentar renovar si estamos en la página de login
      if (window.location.pathname.includes('/login')) {
        console.log('⏭️ Saltando renovación de token - estamos en login');
        return false;
      }
      
      console.log('🔄 Intentando renovar token...');
      
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('❌ No hay token para renovar');
        return false;
      }

      // Crear una instancia temporal sin interceptores para evitar loops
      const tempApi = axios.create({
        baseURL: API_BASE_URL,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`, // Agregar el token directamente en los headers
        },
        timeout: 10000,
      });

      console.log('🔑 Token enviado para renovación:', token.substring(0, 20) + '...');

      const response: AxiosResponse<ApiResponse<{ token: string }>> = await tempApi.post('/auth/refresh');
      const newToken = response.data.data!.token;
      localStorage.setItem('token', newToken);
      console.log('✅ Token renovado exitosamente');
      return true;
    } catch (error: any) {
      console.error('❌ Error renovando token:', error.response?.data || error.message);
      return false;
    }
  }

  async updateProfile(profileData: Partial<User>): Promise<User> {
    const response: AxiosResponse<ApiResponse<{ user: User }>> = await this.api.put('/auth/profile', profileData);
    return response.data.data!.user;
  }

  async changePassword(passwords: { currentPassword: string; newPassword: string }): Promise<void> {
    await this.api.put('/auth/change-password', passwords);
  }

  async logout(): Promise<void> {
    await this.api.post('/auth/logout');
  }

  async forgotPassword(email: string): Promise<any> {
    const response = await this.api.post('/auth/forgot-password', { email });
    return response.data;
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await this.api.post('/auth/reset-password', { token, newPassword });
  }

  // Verificar si el token está próximo a expirar
  private isTokenExpiringSoon(): boolean {
    const token = localStorage.getItem('token');
    if (!token) return true;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expirationTime = payload.exp * 1000; // Convertir a milisegundos
      const currentTime = Date.now();
      const timeUntilExpiration = expirationTime - currentTime;
      
      // Si expira en menos de 1 hora, considerarlo próximo a expirar
      return timeUntilExpiration < 60 * 60 * 1000;
    } catch (error) {
      console.error('Error verificando token:', error);
      return true;
    }
  }

  // Verificar si el token es válido
  private isTokenValid(): boolean {
    const token = localStorage.getItem('token');
    if (!token) return false;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000; // Convertir a segundos
      return payload.exp > currentTime;
    } catch (error) {
      console.error('Error verificando token:', error);
      return false;
    }
  }

  // Métodos de cursos
  async getRecommendedCourses(): Promise<Course[]> {
    const response: AxiosResponse<ApiResponse<{ courses: Course[] }>> = await this.api.get('/courses/recommended');
    return response.data.data!.courses;
  }

  async getAllCourses(params?: {
    category?: string;
    difficulty?: string;
    search?: string;
    page?: number;
    limit?: number;
    includeContent?: boolean;
  }): Promise<{ courses: Course[]; pagination: any }> {
    const response: AxiosResponse<ApiResponse<{ courses: Course[]; pagination: any }>> = await this.api.get('/courses', { params });
    return response.data.data!;
  }

  async getCourse(courseId: string): Promise<Course> {
    const response: AxiosResponse<ApiResponse<{ course: Course; userProgress?: any; isEnrolled: boolean }>> = await this.api.get(`/courses/${courseId}`);
    return response.data.data!.course;
  }

  async getCourseDetails(courseId: string): Promise<{ course: Course; userProgress?: any; isEnrolled: boolean }> {
    const response: AxiosResponse<ApiResponse<{ course: Course; userProgress?: any; isEnrolled: boolean }>> = await this.api.get(`/courses/${courseId}`);
    return response.data.data!;
  }

  async enrollInCourse(courseId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/courses/${courseId}/enroll`);
    return response.data.data;
  }

  async getLesson(courseId: string, lessonId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/courses/${courseId}/lessons/${lessonId}`);
    return response.data.data;
  }

  async completeLesson(courseId: string, lessonId: string, timeSpent: number): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/courses/${courseId}/lessons/${lessonId}/complete`, { timeSpent });
    return response.data.data;
  }

  // Métodos de evaluaciones
  async getCourseEvaluations(courseId: string): Promise<Evaluation[]> {
    const response: AxiosResponse<ApiResponse<{ evaluations: Evaluation[] }>> = await this.api.get(`/evaluations/course/${courseId}`);
    return response.data.data!.evaluations;
  }

  async getEvaluation(evaluationId: string): Promise<any> {
    console.log('📡 API: Solicitando evaluación con ID:', evaluationId);
    const response: AxiosResponse<any> = await this.api.get(`/evaluations/${evaluationId}`);
    console.log('📡 API: Respuesta completa:', response.data);
    
    // El backend devuelve { evaluation: {...} }, no { data: {...} }
    let evaluation;
    if (response.data.evaluation) {
      evaluation = response.data.evaluation;
    } else if (response.data.data) {
      evaluation = response.data.data;
    } else {
      evaluation = response.data;
    }
    
    console.log('📡 API: Evaluación procesada:', evaluation);
    console.log('📡 API: evaluation.id:', evaluation.id);
    console.log('📡 API: evaluation._id:', evaluation._id);
    
    return evaluation;
  }

  async submitEvaluation(evaluationId: string, answers: any[], timeSpent: number): Promise<any> {
    console.log('📤 API: submitEvaluation iniciado');
    console.log('📤 API: evaluationId:', evaluationId);
    console.log('📤 API: answers:', answers);
    console.log('📤 API: timeSpent:', timeSpent);
    console.log('📤 API: URL completa:', `${API_BASE_URL}/evaluations/${evaluationId}/submit`);
    
    const requestData = { answers, timeSpent };
    console.log('📤 API: requestData:', requestData);
    
    try {
      const response: AxiosResponse<any> = await this.api.post(`/evaluations/${evaluationId}/submit`, requestData);
      console.log('📤 API: Respuesta exitosa:', response.data);
      
      // Manejar diferentes formatos de respuesta
      if (response.data && response.data.data) {
        return response.data.data;
      } else {
        return response.data;
      }
    } catch (error: any) {
      console.error('📤 API: Error en submitEvaluation:', error);
      console.error('📤 API: Error response:', error.response);
      console.error('📤 API: Error data:', error.response?.data);
      console.error('📤 API: Error status:', error.response?.status);
      throw error;
    }
  }

  async getEvaluationResults(evaluationId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/evaluations/${evaluationId}/results`);
    return response.data.data;
  }

  // Métodos de progreso
  async getUserProgress(params?: { status?: string }): Promise<Progress[]> {
    const response: AxiosResponse<ApiResponse<{ progress: Progress[] }>> = await this.api.get('/progress', { params });
    return response.data.data!.progress;
  }

  async getCourseProgress(courseId: string): Promise<Progress> {
    const response: AxiosResponse<ApiResponse<{ progress: Progress }>> = await this.api.get(`/progress/course/${courseId}`);
    return response.data.data!.progress;
  }

  // Métodos de vulnerabilidades
  async getUserVulnerabilities(): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/vulnerabilities/me');
    return response.data.data;
  }

  async getCompanyVulnerabilityStats(): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/vulnerabilities/company-stats');
    return response.data.data;
  }

  // Métodos de administración
  async getDashboard(): Promise<DashboardData> {
    const response: AxiosResponse<ApiResponse<DashboardData>> = await this.api.get('/admin/dashboard');
    return response.data.data!;
  }

  async getEmployees(params?: {
    department?: string;
    riskLevel?: string;
    search?: string;
    companyId?: string;
    page?: number;
    limit?: number;
  }): Promise<{ employees: User[]; pagination: any }> {
    const response: AxiosResponse<ApiResponse<{ employees: User[]; pagination: any }>> = await this.api.get('/admin/employees', { params });
    return response.data.data!;
  }

  async getEmployeeDetail(employeeId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<{ user: any }>> = await this.api.get(`/admin/employees/${employeeId}`);
    return response.data.data!.user;
  }

  async addVulnerability(employeeId: string, vulnerability: {
    type: string;
    severity: string;
    description: string;
  }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/admin/employees/${employeeId}/vulnerabilities`, vulnerability);
    return response.data.data;
  }

  async resolveVulnerability(employeeId: string, vulnerabilityId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/admin/employees/${employeeId}/vulnerabilities/${vulnerabilityId}/resolve`);
    return response.data.data;
  }

  async getReports(params?: {
    startDate?: string;
    endDate?: string;
    type?: string;
  }): Promise<ReportData> {
    const response: AxiosResponse<ApiResponse<{ report: ReportData }>> = await this.api.get('/admin/reports', { params });
    return response.data.data!.report;
  }

  // Métodos de gestión de usuarios (solo para admins)
  async createUser(userData: any): Promise<User> {
    const response: AxiosResponse<ApiResponse<{ user: User }>> = await this.api.post('/users', userData);
    return response.data.data!.user;
  }

  async updateUser(userId: string, userData: any): Promise<User> {
    const response: AxiosResponse<ApiResponse<{ user: User }>> = await this.api.put(`/users/${userId}`, userData);
    return response.data.data!.user;
  }

  // Métodos de gestión de empresas (solo para super admins)
  async getCompanies(params?: {
    search?: string;
    plan?: string;
    isActive?: boolean | string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<{ companies: any[]; pagination: any }> {
    const response: AxiosResponse<ApiResponse<{ companies: any[]; pagination: any }>> = await this.api.get('/companies', { params });
    return response.data.data!;
  }

  async getCompany(companyId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<{ company: any }>> = await this.api.get(`/companies/${companyId}`);
    return response.data.data!.company;
  }

  async createCompany(companyData: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<{ company: any }>> = await this.api.post('/companies', companyData);
    return response.data.data!.company;
  }

  async updateCompany(companyId: string, companyData: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<{ company: any }>> = await this.api.put(`/companies/${companyId}`, companyData);
    return response.data.data!.company;
  }

  async updateCompanyStatus(companyId: string, isActive: boolean): Promise<any> {
    const response: AxiosResponse<ApiResponse<{ company: any }>> = await this.api.put(`/companies/${companyId}/status`, { isActive });
    return response.data.data!.company;
  }

  async updateCompanySubscription(companyId: string, subscriptionData: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<{ company: any }>> = await this.api.put(`/companies/${companyId}/subscription`, subscriptionData);
    return response.data.data!.company;
  }

  async getCompanyUsers(companyId: string, params?: {
    isActive?: boolean;
    role?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{ users: User[]; pagination: any }> {
    const response: AxiosResponse<ApiResponse<{ users: User[]; pagination: any }>> = await this.api.get(`/companies/${companyId}/users`, { params });
    return response.data.data!;
  }

  async getCompanyStatsById(companyId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<{ company: any; stats: any }>> = await this.api.get(`/companies/${companyId}/stats`);
    return response.data.data;
  }

  // Métodos de gestión de cursos (solo para admins)
  async createCourse(courseData: any): Promise<Course> {
    const response: AxiosResponse<ApiResponse<{ course: Course }>> = await this.api.post('/courses', courseData);
    return response.data.data!.course;
  }

  async updateCourse(courseId: string, courseData: any): Promise<Course> {
    const response: AxiosResponse<ApiResponse<{ course: Course }>> = await this.api.put(`/courses/${courseId}`, courseData);
    return response.data.data!.course;
  }

  async deleteCourse(courseId: string): Promise<void> {
    await this.api.delete(`/courses/${courseId}`);
  }

  // Métodos de gestión de evaluaciones (solo para admins)
  async createEvaluation(evaluationData: any): Promise<Evaluation> {
    try {
      const response: AxiosResponse<any> = await this.api.post('/evaluations', evaluationData);
      
      // Manejar diferentes formatos de respuesta
      if (response.data && response.data.data && response.data.data.evaluation) {
        // Formato: { data: { evaluation: ... } }
        return response.data.data.evaluation;
      } else if (response.data && response.data.evaluation) {
        // Formato: { evaluation: ... }
        return response.data.evaluation;
      } else {
        // Formato directo
        return response.data;
      }
    } catch (error: any) {
      console.error('Error en createEvaluation:', error);
      throw error;
    }
  }

  async updateEvaluation(evaluationId: string, evaluationData: any): Promise<Evaluation> {
    try {
      console.log('🔧 updateEvaluation - Iniciando...');
      console.log('🔧 ID:', evaluationId);
      console.log('🔧 Datos:', evaluationData);
      
      const response: AxiosResponse<any> = await this.api.put(`/evaluations/${evaluationId}`, evaluationData);
      
      console.log('🔧 Respuesta completa:', response);
      console.log('🔧 response.data:', response.data);
      console.log('🔧 response.status:', response.status);
      
      // Manejar diferentes formatos de respuesta
      if (response.data && response.data.data && response.data.data.evaluation) {
        console.log('🔧 Formato 1: { data: { evaluation: ... } }');
        return response.data.data.evaluation;
      } else if (response.data && response.data.evaluation) {
        console.log('🔧 Formato 2: { evaluation: ... }');
        return response.data.evaluation;
      } else {
        console.log('🔧 Formato 3: respuesta directa');
        return response.data;
      }
    } catch (error: any) {
      console.error('❌ Error en updateEvaluation:', error);
      console.error('❌ Error response:', error.response);
      console.error('❌ Error data:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      throw error;
    }
  }

  async updateEvaluationStatus(evaluationId: string, isActive: boolean): Promise<Evaluation> {
    try {
      console.log('🔧 updateEvaluationStatus - Iniciando...');
      console.log('🔧 ID:', evaluationId);
      console.log('🔧 isActive:', isActive);
      
      const response: AxiosResponse<any> = await this.api.patch(`/evaluations/${evaluationId}/status`, { isActive });
      
      console.log('🔧 Respuesta completa:', response);
      console.log('🔧 response.data:', response.data);
      
      // Manejar diferentes formatos de respuesta
      if (response.data && response.data.data && response.data.data.evaluation) {
        return response.data.data.evaluation;
      } else if (response.data && response.data.evaluation) {
        return response.data.evaluation;
      } else {
        return response.data;
      }
    } catch (error: any) {
      console.error('❌ Error en updateEvaluationStatus:', error);
      console.error('❌ Error response:', error.response);
      console.error('❌ Error data:', error.response?.data);
      throw error;
    }
  }

  async getEvaluationStats(evaluationId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/evaluations/${evaluationId}/stats`);
    return response.data.data;
  }

  // Métodos para mantenimiento de evaluaciones
  async getEvaluations(): Promise<{ evaluations: Evaluation[] }> {
    const response: AxiosResponse<{ evaluations: Evaluation[] }> = await this.api.get('/evaluations/admin');
    return response.data;
  }

  async getEvaluationById(evaluationId: string): Promise<{ evaluation: Evaluation }> {
    const response: AxiosResponse<{ evaluation: Evaluation }> = await this.api.get(`/evaluations/${evaluationId}`);
    return response.data;
  }

  async deleteEvaluation(evaluationId: string): Promise<void> {
    await this.api.delete(`/evaluations/${evaluationId}`);
  }

  // Métodos de administración de empresa
  async getCompanyEmployees(params?: {
    page?: number;
    limit?: number;
    search?: string;
    department?: string;
    office?: string;
    status?: string;
    companyId?: string;
    dateFrom?: string;
    dateTo?: string;
    /** all | never (sin lastLogin) | logged */
    lastAccess?: string;
    /** all | corporate | invited */
    accountType?: string;
    role?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<{ employees: any[]; pagination: any }> {
    const response: AxiosResponse<ApiResponse<{ employees: any[]; pagination: any }>> = await this.api.get('/company-admin/employees', { params });
    return response.data.data!;
  }

  async getCompanyEmployeeDetail(employeeId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/company-admin/employees/${employeeId}`);
    return response.data.data;
  }

  async updateCompanyEmployee(employeeId: string, employeeData: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/company-admin/employees/${employeeId}`, employeeData);
    return response.data.data;
  }

  async updateCompanyEmployeeStatus(employeeId: string, isActive: boolean): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/company-admin/employees/${employeeId}/status`, { isActive });
    return response.data.data;
  }

  async unlockEmployee(employeeId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/company-admin/employees/${employeeId}/unlock`);
    return response.data;
  }

  async resendWelcomeEmails(employeeIds: string[]): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/company-admin/employees/resend-welcome', { employeeIds });
    return response.data;
  }

  async getEmployeesPendingCourses(params?: {
    page?: number;
    limit?: number;
    search?: string;
    department?: string;
    office?: string;
    status?: string;
    course?: string;
  }): Promise<{ rows: any[]; courses: any[]; offices: any[]; departments: any[]; pagination: any }> {
    const response: AxiosResponse<ApiResponse<{ rows: any[]; courses: any[]; offices: any[]; departments: any[]; pagination: any }>> = await this.api.get('/company-admin/employees-pending-courses', { params });
    return response.data.data!;
  }

  async sendCourseReminders(params: {
    search?: string;
    department?: string;
    office?: string;
    status?: string;
    course?: string;
  }): Promise<{ sent: number; failed: number; total: number; errors: any[] }> {
    const response: AxiosResponse<ApiResponse<{ sent: number; failed: number; total: number; errors: any[] }>> =
      await this.api.post('/company-admin/employees-pending-courses/send-reminders', params, {
        timeout: 300000
      });
    return response.data.data!;
  }

  async unlockUser(userId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/admin/users/${userId}/unlock`);
    return response.data;
  }

  async generateEmployeePasswordResetLink(
    employeeId: string,
    options?: { sendEmail?: boolean }
  ): Promise<{
    resetToken: string;
    email: string;
    firstName: string;
    lastName: string;
    companyName?: string | null;
    emailSent: boolean;
    expiresIn: string;
  }> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      `/admin/employees/${employeeId}/reset-password-link`,
      { sendEmail: options?.sendEmail === true }
    );
    return response.data.data!;
  }

  async sendEmployeePasswordResetEmail(
    employeeId: string,
    resetToken: string
  ): Promise<{ emailSent: boolean; email: string }> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(
      `/admin/employees/${employeeId}/send-password-reset-email`,
      { resetToken }
    );
    return response.data.data!;
  }

  async getCompanyAdminStats(): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/company-admin/stats');
    return response.data.data;
  }

  async getDepartmentProgress(): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/company-admin/department-progress');
    return response.data.data;
  }

  // Admin functions
  async getAdminEmployees(params: any = {}): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/admin/employees', { params });
    return response.data.data;
  }

  async getAdminEmployeeDetail(employeeId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/admin/employees/${employeeId}`);
    return response.data.data;
  }

  // Department functions
  async getDepartments(companyId?: string): Promise<any> {
    const params = companyId ? { companyId } : {};
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/departments', { params });
    return response.data.data;
  }

  async createDepartment(departmentData: any, companyId?: string): Promise<any> {
    const data = companyId ? { ...departmentData, companyId } : departmentData;
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/departments', data);
    return response.data.data;
  }

  async updateDepartment(departmentId: string, departmentData: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/departments/${departmentId}`, departmentData);
    return response.data.data;
  }

  async deleteDepartment(departmentId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/departments/${departmentId}`);
    return response.data.data;
  }

  async getDepartmentEmployees(departmentId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/departments/${departmentId}/employees`);
    return response.data.data;
  }

  async getDepartmentStats(departmentId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/departments/${departmentId}/stats`);
    return response.data.data;
  }

  // Office functions
  async getOffices(companyId?: string): Promise<any> {
    const params = companyId ? { companyId } : {};
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/offices', { params });
    return response.data.data;
  }

  async createOffice(officeData: any, companyId?: string): Promise<any> {
    const data = companyId ? { ...officeData, companyId } : officeData;
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/offices', data);
    return response.data.data;
  }

  async updateOffice(officeId: string, officeData: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/offices/${officeId}`, officeData);
    return response.data.data;
  }

  async deleteOffice(officeId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/offices/${officeId}`);
    return response.data.data;
  }

  async getOfficeEmployees(officeId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/offices/${officeId}/employees`);
    return response.data.data;
  }

  async getOfficeStats(officeId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/offices/${officeId}/stats`);
    return response.data.data;
  }

  async getOfficeProgress(): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/company-admin/office-progress');
    return response.data.data;
  }

  async getAllResults(companyId?: string): Promise<any[]> {
    const params = companyId ? { companyId } : {};
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/company-admin/all-results', { params });
    return response.data.data!;
  }

  // Domain Request functions
  async createDomainRequest(requestData: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/domain-requests', requestData);
    return response.data.data;
  }

  async getDomainRequests(params: any = {}): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/domain-requests', { params });
    return response.data.data;
  }

  async getDomainRequest(requestId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/domain-requests/${requestId}`);
    return response.data.data;
  }

  async updateDomainRequest(requestId: string, data: { phone?: string; companyName?: string; message?: string }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/domain-requests/${requestId}`, data);
    return response.data.data;
  }

  async updateDomainRequestByEmail(email: string, data: { phone?: string; companyName?: string; message?: string }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/domain-requests/by-email/${encodeURIComponent(email)}`, data);
    return response.data.data;
  }

  async updateDomainRequestStatus(requestId: string, status: string, note?: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/domain-requests/${requestId}/status`, { status, note });
    return response.data.data;
  }

  async assignDomainRequest(requestId: string, assignedTo: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/domain-requests/${requestId}/assign`, { assignedTo });
    return response.data.data;
  }

  async addDomainRequestNote(requestId: string, note: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/domain-requests/${requestId}/notes`, { note });
    return response.data.data;
  }

  async deleteDomainRequest(requestId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/domain-requests/${requestId}`);
    return response.data.data;
  }

  // Email verification methods
  async verifyEmail(token: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/email-verification/verify/${token}`);
    return response.data;
  }

  async sendVerificationEmail(email: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/email-verification/send', { email });
    return response.data;
  }

  async getEmailVerificationStatus(email: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/email-verification/status/${email}`);
    return response.data;
  }

  // User management methods
  async toggleUserStatus(userId: string, isActive: boolean): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/admin/users/${userId}/status`, { isActive });
    return response.data.data;
  }

  async updateUserRole(userId: string, role: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/admin/users/${userId}/role`, { role });
    return response.data;
  }

  async getCompanyInfo(): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/company-admin/company');
    return response.data.data;
  }

  async updateCompanyInfo(companyData: any): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put('/company-admin/company', companyData);
    return response.data.data;
  }

  async updateCompanyDomains(companyId: string, authorizedDomains: string[]): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/companies/${companyId}/domains`, { authorizedDomains });
    return response.data.data;
  }

  // Método para subir video
  async uploadVideo(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('video', file);
    
    const response: AxiosResponse<ApiResponse<{ url: string }>> = await this.api.post('/upload/video/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data!.url;
  }

  // Método para subir logo de empresa
  // Bulk upload functions
  async validateBulkUpload(file: FormData): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/bulk-upload/validate', file, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data;
  }

  async createBulkEmployees(employees: any[]): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/bulk-upload/create', { employees });
    return response.data.data;
  }

  async validateBulkUploadDepartments(file: FormData): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/bulk-upload/departments/validate', file, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data;
  }

  async createBulkDepartments(departments: any[]): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/bulk-upload/departments/create', { departments });
    return response.data.data;
  }

  async validateBulkUploadOffices(file: FormData): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/bulk-upload/offices/validate', file, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data;
  }

  async createBulkOffices(offices: any[]): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/bulk-upload/offices/create', { offices });
    return response.data.data;
  }

  async setPassword(token: string, password: string): Promise<void> {
    await this.api.post('/auth/set-password', { token, newPassword: password });
  }

  async uploadLogo(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('logo', file);
    
    console.log('Subiendo logo:', file.name, file.size, file.type);
    
    const response: AxiosResponse<ApiResponse<{ url: string }>> = await this.api.post('/upload/logo/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    console.log('Respuesta del upload:', response.data);
    
    if (!response.data.data || !response.data.data.url) {
      throw new Error('Respuesta inválida del servidor: no se recibió URL del logo');
    }
    
    return response.data.data.url;
  }

  async uploadSignature(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('signature', file);
    
    console.log('Subiendo firma:', file.name, file.size, file.type);
    
    const response: AxiosResponse<ApiResponse<{ url: string }>> = await this.api.post('/upload/signature/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    console.log('Respuesta del upload:', response.data);
    
    if (!response.data.data || !response.data.data.url) {
      throw new Error('Respuesta inválida del servidor: no se recibió URL de la firma');
    }
    
    return response.data.data.url;
  }

  // Métodos de certificados
  async generateCertificate(courseId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/certificates/generate', { courseId });
    return response.data.data;
  }

  // Métodos de analíticas
  async getCompanyAnalytics(status?: 'active' | 'inactive' | 'all'): Promise<any> {
    const params = status ? { status } : {};
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/analytics/companies', { params });
    return response.data.data;
  }

  async getCompanyAnalyticsDetail(companyId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/analytics/companies/${companyId}`);
    return response.data.data;
  }

  // Método para obtener detalles de cursos de un usuario específico
  async getUserCourseDetails(userId: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/analytics/user/${userId}/courses`);
    return response.data.data;
  }

  async getMyCertificates(): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/certificates/my-certificates');
    return response.data.data!;
  }

  async getCompanyCertificates(companyId?: string): Promise<any[]> {
    const params = companyId ? { companyId } : {};
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/certificates/company', { params });
    return response.data.data!;
  }
}

// Instancia singleton del servicio
const apiService = new ApiService();

export default apiService; 